import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, ArrowRight, CalendarDays, Heart, BedDouble, UtensilsCrossed, Compass, Wine } from "lucide-react";
import { useLang, L } from "@/i18n";
import { api } from "@/api";
import { SECTIONS } from "@/config";
import { MarqueeRibbon, WaveDivider, SectionHead, Reveal, HeartButton } from "@/components/Widgets";
import WeatherWidget from "@/components/WeatherWidget";
import ItemCard from "@/components/ItemCard";

const HERO_IMG = "https://images.unsplash.com/photo-1749482154340-f47cf182c3c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHx0dXNjYW55JTIwY29hc3QlMjBiZWFjaHxlbnwwfHx8fDE3ODcxNDI1ODR8MA&ixlib=rb-4.1.0&q=85";

function KineticLine({ children, delay }) {
  return (
    <span className="masked-line">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    api.get("/items", { params: { type: "event", upcoming: true, limit: 4 } })
      .then((r) => setEvents(r.data.slice(0, 4)))
      .catch(() => {});
    api.get("/items", { params: { limit: 40 } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
  }, []);

  const sponsoredPool = featured.filter((i) => i.sponsored && i.images?.length && i.type !== "event");
  const organic = featured.filter((i) => !i.sponsored && i.type !== "event" && i.images?.length);
  const rot = sponsoredPool.length ? Math.floor(Date.now() / 300000) % sponsoredPool.length : 0;
  const sponsoredTiles = sponsoredPool.length
    ? [...new Set([sponsoredPool[rot], sponsoredPool[(rot + 1) % sponsoredPool.length]])]
        .slice(0, 2)
        .map((item) => ({ kind: "item", item, big: false, sponsored: true }))
    : [];
  const catTiles = ["restaurant", "accommodation", "activity", "offer"]
    .map((tp) => ({ kind: "cat", s: SECTIONS.find((x) => x.type === tp) }));
  const mosaicTiles = [
    ...organic.slice(0, 1).map((item) => ({ kind: "item", item, big: true })),
    ...sponsoredTiles,
    ...catTiles.slice(0, 2),
    ...organic.slice(1, 2).map((item) => ({ kind: "item", item, big: true })),
    ...catTiles.slice(2),
    ...organic.slice(2, 6).map((item) => ({ kind: "item", item, big: false })),
  ];

  const manifesto = [
    { n: "01", t: t("home.m1t"), d: t("home.m1d") },
    { n: "02", t: t("home.m2t"), d: t("home.m2d") },
    { n: "03", t: t("home.m3t"), d: t("home.m3d") },
  ];

  const fmtDay = (iso) => new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "short" });

  return (
    <div data-testid="home-page">
      {/* MASTHEAD — banda sottile: territorio a sinistra, meteo a destra */}
      <section className="border-b border-black/5" data-testid="masthead">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
          <h1 className="font-heading font-semibold uppercase text-lg sm:text-xl tracking-wide text-mare truncate" data-testid="masthead-title">
            Golfo di Follonica · {t("hero.mastSub")}
          </h1>
          <div className="shrink-0 hidden sm:flex">
            <WeatherWidget compact />
          </div>
        </div>
      </section>

      {/* STRISCIA PROSSIMO EVENTO — banda che si stacca dalla pagina */}
      {events[0] && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6" data-testid="home-event-strip">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-3xl bg-mare text-calce shadow-[0_24px_60px_rgba(15,76,131,0.35)] grid md:grid-cols-2"
          >
            <div className="p-6 sm:p-10 flex flex-col justify-center order-2 md:order-1">
              <span className="inline-flex self-start items-center gap-2 rounded-full bg-terracotta text-white text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 shadow">
                <CalendarDays className="w-4 h-4" /> {t("hero.nextEvent")} · {new Date(events[0].start).toLocaleDateString(lang, { day: "numeric", month: "long" })}
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-4 leading-tight">{L(events[0].title, lang)}</h2>
              <p className="mt-3 text-calce/85 text-sm sm:text-base line-clamp-2">{L(events[0].excerpt, lang)}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/scheda/${events[0].slug}`} data-testid="strip-event-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#d96d00] text-white font-bold px-5 py-3 text-sm transition-colors">
                  {t("hero.discoverEvent")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/eventi" data-testid="strip-all-events"
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-calce font-bold px-5 py-3 text-sm transition-colors">
                  {t("home.allEvents")}
                </Link>
              </div>
              {events.length > 1 && (
                <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="strip-queue">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sabbia/90">{t("hero.after")}</span>
                  {events.slice(1, 4).map((ev) => (
                    <Link key={ev.slug} to={`/scheda/${ev.slug}`} data-testid={`strip-queue-${ev.slug}`}
                      className="rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-calce text-[11px] font-semibold px-3 py-1.5 transition-colors">
                      {fmtDay(ev.start)} · {L(ev.title, lang)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="relative h-56 md:h-auto order-1 md:order-2">
              {events[0].images?.[0] && (
                <img src={events[0].images[0]} alt={L(events[0].title, lang)} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-mare/70 to-transparent" />
            </div>
          </motion.div>
        </section>
      )}

      {/* QUICK SEARCH */}
      <div className="relative z-20 -mt-8 px-4 sm:px-6">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.6 }}
          onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/cerca?q=${encodeURIComponent(q.trim())}`); }}
          className="max-w-2xl mx-auto bg-white rounded-full shadow-[0_16px_50px_rgba(14,110,140,0.18)] flex items-center p-2 gap-2"
        >
          <Search className="w-5 h-5 text-mare ml-3 shrink-0" />
          <input
            data-testid="home-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent outline-none text-sm sm:text-base py-2 text-grafite placeholder:text-grafite/40"
          />
          <button type="submit" data-testid="home-search-button"
            className="rounded-full bg-mare hover:bg-[#0b5a73] text-white font-bold px-5 py-2.5 text-sm transition-colors shrink-0">
            {t("common.discover")}
          </button>
        </motion.form>
      </div>

      <div className="mt-14">
        <MarqueeRibbon items={["Follonica", "Golfo di Follonica", "Maremma Toscana", "Il territorio che si merita un like", "Mare · Pineta · Entroterra"]} />
      </div>

      {/* MOSAICO — panoramica stile portale, subito visibile */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="home-mosaic">
        <SectionHead kicker="Follonica Like" title={t("home.explore")} sub={t("home.exploreSub")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[150px] md:auto-rows-[170px]">
          {mosaicTiles.map((tile, i) => {
            const Icon = tile.kind === "cat" ? tile.s.icon : null;
            return tile.kind === "cat" ? (
              <Reveal key={tile.s.type} delay={i * 0.04} className="h-full">
                <Link to={tile.s.path} data-testid={`tile-${tile.s.type}`}
                  className="card-lift flex flex-col justify-between h-full rounded-3xl p-5 text-white"
                  style={{ background: tile.s.color }}>
                  <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-heading font-bold text-lg leading-tight">{t(tile.s.navKey)}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-white/80 mt-1">
                      {t("common.discover")} <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ) : (
              <Reveal key={tile.item.slug} delay={i * 0.04} className={`h-full ${tile.big ? "col-span-2 row-span-2" : ""}`}>
                <Link to={`/scheda/${tile.item.slug}`} data-testid={`tile-item-${tile.item.slug}`}
                  className="card-lift group relative block h-full rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(15,76,131,0.12)]">
                  <img src={tile.item.images[0]} alt={L(tile.item.title, lang)} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                  <span className={`absolute top-3 left-3 rounded-full text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 ${tile.sponsored ? "bg-sabbia text-grafite" : "bg-terracotta text-white"}`}>
                    {tile.sponsored ? t("badge.sponsored") : (tile.item.category || tile.item.type)}
                  </span>
                  <div className="absolute bottom-0 p-4 w-full">
                    <p className={`font-heading font-semibold text-calce text-shadow-hero leading-tight ${tile.big ? "text-2xl" : "text-base"}`}>
                      {L(tile.item.title, lang)}
                    </p>
                    {tile.big && <p className="text-calce/80 text-sm mt-1 line-clamp-2">{L(tile.item.excerpt, lang)}</p>}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>



      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta mb-10">{t("home.manifesto")}</p>
        <div className="grid gap-14">
          {manifesto.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className={`grid md:grid-cols-12 gap-6 items-baseline ${i % 2 ? "md:text-right" : ""}`}>
                <span className={`font-heading font-bold text-6xl sm:text-7xl text-turchese/50 md:col-span-3 ${i % 2 ? "md:order-2" : ""}`}>{m.n}</span>
                <div className="md:col-span-9">
                  <h3 className="font-heading font-bold text-2xl sm:text-3xl text-grafite">{m.t}</h3>
                  <p className="mt-3 text-grafite/70 text-base sm:text-lg leading-relaxed max-w-2xl md:max-w-none">{m.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SURVEY BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-pineta text-calce p-8 sm:p-12" data-testid="home-survey-banner">
            <Heart className="absolute -right-8 -top-8 w-48 h-48 text-white/5 fill-white/5" />
            <p className="font-accent text-2xl text-sabbia mb-2">psst…</p>
            <h3 className="font-heading font-bold text-2xl sm:text-3xl max-w-xl">{t("home.surveyTitle")}</h3>
            <p className="mt-3 text-calce/80 max-w-xl">{t("home.surveySub")}</p>
            <Link to="/sondaggio" data-testid="home-survey-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#d15f2d] text-white font-bold px-6 py-3.5 transition-colors">
              {t("home.surveyCta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
